import type { Button } from "../input";
import type { RideId } from "../sim/rides";
import { FOOT_RUN, FOOT_TURN, TAG_WASH_TIME } from "../sim/tuning";

// The demo lines' types and the script helpers every map's lines are written
// with (`demo-lines/<map>.ts`). The player is in `demo.ts`.

/** The sim's fixed step. Game uses the same one. */
export const DEMO_STEP = 1 / 120;

export interface DemoSegment {
  /** Seconds this segment lasts. */
  dur: number;
  /** Stick, -1..1. Left unset while grinding / manualing, it balances. */
  x?: number;
  y?: number;
  hold?: Button[];
}

export interface DemoLine {
  id: string;
  /** Shown on the DEMO badge. */
  name: string;
  mapId: string;
  start: { x: number; z: number; yaw: number; speed: number };
  /** Start with the special meter full (for lines that show a special). */
  special?: boolean;
  segments: DemoSegment[];
  /** Tricks the line must land (each appears in a landed combo), checked by tests. */
  lands: string[];
  /** Paintballs the line must fire (#136), each splatting the park or bursting a target, checked by tests. */
  shots?: number;
  /** Paint splats the line must wash off with the power washer (#151), checked by tests. */
  washed?: number;
  /** Paintball targets (#137) the line must burst, checked by tests. */
  targets?: number;
  /** Graffiti tags (#151) the line must wash clean, checked by tests. */
  cleans?: number;
  /** Cones and cans (#151) the line must knock over with the power washer, checked by tests. */
  knocks?: number;
  /** The ride (#141) the line must end up on, checked by tests. */
  ride?: RideId;
}

// Script helpers, so lines read like button presses.
export const roll = (dur: number, x = 0): DemoSegment => ({ dur, x, y: 0 });
/** Rolls without touching the stick: balances while grinding or manualing. */
export const ride = (dur: number): DemoSegment => ({ dur });
export const crouch = (dur: number, x = 0): DemoSegment => ({
  dur,
  x,
  y: 0,
  hold: ["ollie"],
});
/**
 * A button tapped for one step with the stick held somewhere. One step, since
 * a stick held sideways in the air spins you.
 */
export const tap = (button: Button, x = 0, y = 0): DemoSegment => ({
  dur: DEMO_STEP,
  x,
  y,
  hold: [button],
});
export const hold = (
  button: Button,
  dur: number,
  x = 0,
  y = 0,
): DemoSegment => ({ dur, x, y, hold: [button] });
/** Crouched, two quick stick taps then let go: a Boneless (up, up) or Fastplant (down, down). */
export const popVariant = (y: number, crouchFor = 0.2): DemoSegment[] => [
  { dur: crouchFor, x: 0, y: 0, hold: ["ollie"] },
  { dur: 0.05, x: 0, y, hold: ["ollie"] },
  { dur: 0.05, x: 0, y: 0, hold: ["ollie"] },
  { dur: 0.05, x: 0, y, hold: ["ollie"] },
];
/** Up held on the stick (a transfer out over the deck at a vert launch, #77), crouched if `crouched`. */
export const up = (dur: number, crouched = false): DemoSegment => ({
  dur,
  x: 0,
  y: 1,
  hold: crouched ? ["ollie"] : [],
});
/** Two quick stick presses (manuals, specials). */
export const flick = (
  a: [number, number],
  b: [number, number],
): DemoSegment[] => [
  { dur: 0.05, x: a[0], y: a[1] },
  { dur: 0.05, x: b[0], y: b[1] },
];

/** L2 then R2 a moment later: steps off the board, or back on (#19). */
export const stepOff = (): DemoSegment[] => [
  { dur: 0.05, x: 0, y: 0, hold: ["nollie"] },
  { dur: 0.05, x: 0, y: 0, hold: ["nollie", "revert"] },
  roll(0.1),
];

export const N = Math.PI; // yaw facing north (-z)
export const W = -Math.PI / 2; // facing west (-x)
export const E = Math.PI / 2; // facing east (+x)

/**
 * A map's target row (#137, its map file's `targets`) and the spot its demo
 * line shoots it from (`targets.test.ts`' ranges), for `targetsLine`.
 */
export interface TargetRow {
  range: { x: number; z: number };
  row: readonly (readonly [number, number])[];
}

/**
 * Standing on foot with the gun out, turns to face each heading in turn
 * (the stick all the way, at `FOOT_TURN`) and fires at it.
 */
function shootRow(from: number, yaws: readonly number[]): DemoSegment[] {
  const out: DemoSegment[] = [];
  let yaw = from;
  for (const want of yaws) {
    out.push(...turnOnFoot(yaw, want), { dur: 0.1, x: 0, y: 0 }, tap("flip"), {
      dur: 0.35,
      x: 0,
      y: 0,
    });
    yaw = want;
  }
  return out;
}

/** On foot, turns on the spot from heading `from` to `to` the short way (the stick all the way, at `FOOT_TURN`). */
function turnOnFoot(from: number, to: number): DemoSegment[] {
  const d = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  // Steering right (+x) turns the yaw down.
  return Math.abs(d) > 0.01
    ? [{ dur: Math.abs(d) / FOOT_TURN, x: d > 0 ? -1 : 1, y: 0 }]
    : [];
}

/**
 * The targets (#137): off the board at the map's range, the gun out, then a
 * turn and a shot at each of the five in its row (the auto-aim tidies up
 * the last few degrees), then round to face `back` and on the board again,
 * braking (down) unless `brake` is false, so it stops short of a wall.
 * `rows` is the map's target row and the spot it's shot from.
 */
/* @__NO_SIDE_EFFECTS__ */
export function targetsLine(
  mapId: string,
  rows: TargetRow,
  name: string,
  back: number,
  brake = true,
): DemoLine {
  const { range, row } = rows;
  const aimAt = ([x, z]: readonly [number, number]): number =>
    Math.atan2(x - range.x, z - range.z);
  const yaws = row.map(aimAt);
  return {
    id: `${mapId}-targets`,
    name,
    mapId,
    start: { ...range, yaw: yaws[0]!, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      { dur: 0.3, x: 0, y: 0 },
      tap("spinR"),
      { dur: 0.3, x: 0, y: 0 },
      ...shootRow(yaws[0]!, yaws),
      ...turnOnFoot(yaws[yaws.length - 1]!, back),
      { dur: 0.3, x: 0, y: 0 },
      ...stepOff(),
      brake ? { dur: 2, x: 0, y: -1 } : roll(1),
    ],
    lands: [],
    shots: row.length,
    targets: row.length,
  };
}

/** On foot, walks `d` m the way you face and stops: from about 2.5 m, speeding up and slowing down cancel out. */
function walkOnFoot(d: number): DemoSegment[] {
  return [
    { dur: d / FOOT_RUN, x: 0, y: 1 },
    { dur: 0.5, x: 0, y: 0 },
  ];
}

/**
 * A map's graffiti (#151, its map file's `graffiti`) for `graffitiLine`:
 * the heading that faces the fence they're on, and a spot in front of each
 * tag, in the order they're washed, as far out as the jet drops to its
 * middle (`demo.test.ts` checks the line washes every one).
 */
export interface TagWall {
  face: number;
  stands: readonly (readonly [number, number])[];
}

/**
 * The graffiti (#151): off the board at the first tag, R1 round the pack to
 * the power washer, and □ held on each tag until it's clean, walking to the
 * next in between. Ends on foot.
 */
/* @__NO_SIDE_EFFECTS__ */
export function graffitiLine(
  mapId: string,
  wall: TagWall,
  name: string,
): DemoLine {
  const { face, stands } = wall;
  const [x, z] = stands[0]!;
  const segments: DemoSegment[] = [
    roll(0.2),
    ...stepOff(),
    { dur: 0.3, x: 0, y: 0 },
  ];
  for (let i = 0; i < 3; i++)
    segments.push(tap("spinR"), { dur: 0.3, x: 0, y: 0 });
  stands.forEach(([sx, sz], i) => {
    segments.push(hold("flip", TAG_WASH_TIME + 0.15), { dur: 0.2, x: 0, y: 0 });
    const next = stands[i + 1];
    if (!next) return;
    const way = Math.atan2(next[0] - sx, next[1] - sz);
    segments.push(
      ...turnOnFoot(face, way),
      ...walkOnFoot(Math.hypot(next[0] - sx, next[1] - sz)),
      ...turnOnFoot(way, face),
    );
  });
  return {
    id: `${mapId}-graffiti`,
    name,
    mapId,
    start: { x, z, yaw: face, speed: 0 },
    segments,
    lands: [],
    cleans: stands.length,
  };
}

/**
 * Knocking things about with the power washer (#151): off the board at
 * `stand`, R1 round the pack to the washer, then turned to each of `aims`
 * (a cone or can, 4-5 m off) in turn with □ held until it's skidding off.
 * Ends on foot.
 */
/* @__NO_SIDE_EFFECTS__ */
export function propsLine(
  id: string,
  mapId: string,
  stand: readonly [number, number],
  face: number,
  aims: readonly (readonly [number, number])[],
  name: string,
): DemoLine {
  const segments: DemoSegment[] = [roll(0.2), ...stepOff(), { dur: 0.3, x: 0, y: 0 }];
  for (let i = 0; i < 3; i++) segments.push(tap("spinR"), { dur: 0.3, x: 0, y: 0 });
  let yaw = face;
  for (const [x, z] of aims) {
    const way = Math.atan2(x - stand[0], z - stand[1]);
    segments.push(...turnOnFoot(yaw, way), hold("flip", 1.2), { dur: 0.6, x: 0, y: 0 });
    yaw = way;
  }
  return {
    id,
    name,
    mapId,
    start: { x: stand[0], z: stand[1], yaw: face, speed: 0 },
    segments,
    lands: [],
    knocks: aims.length,
  };
}
